import { fn } from '@ember/helper';
import { on } from '@ember/modifier';
import { action } from '@ember/object';
import type Owner from '@ember/owner';
import { next, schedule } from '@ember/runloop';
import { service } from '@ember/service';
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';

import $ from 'jquery';

import type { Service } from 'webapp-types';
import { NewReportSlideLayout } from 'webapp/enums/enums';
import { TourId } from 'webapp/enums/tour';
import { sortBy } from 'webapp/utils/common';

import eq from 'webapp/helpers/eq';
import didInsert from 'webapp/modifiers/did-insert';
import didUpdate from 'webapp/modifiers/did-update';
import onWindowEvents from 'webapp/modifiers/on-window-events';
import willDestroy from 'webapp/modifiers/will-destroy';
import BoardImagePreviewComponent from 'webapp/components/board/image-preview';
import CommonUiIconComponent from 'webapp/components/common/ui-icon';
import ReportsEditorNavigationAddComponent from 'webapp/components/reports/editor/navigation/add';
import ReportsPopupSlideTypeComponent from 'webapp/components/reports/popup-slide-type';

import type Report from 'webapp/models-custom/report';
import type ReportSlide from 'webapp/models-custom/report-slide';
import type ReportSlideRecord from 'webapp/models/report-slide';

import type { SlidesTourSettings } from 'webapp/services/flow/tour/reports-slides';

interface Args {
	model: Report;
	selectedSlide?: ReportSlide;
	onAddNewSlide: (type: NewReportSlideLayout) => Promise<ReportSlideRecord>;
	onOpenSlide: (slide: ReportSlide) => void;
	onRemoveSlide: (slide: ReportSlide) => void;
}

export default class ReportsEditorNavigationComponent extends Component<Args> {
	node!: HTMLElement;
	listEl!: JQuery;

	@service('event-manager') declare eventManagerService: Service.EventManager;
	@service('popup') declare popupService: Service.Popup;
	@service('request') declare requestService: Service.Request;
	@service('analytics') declare analyticsService: Service.Analytics;
	@service('flow/tour/reports-slides')
	declare flowTourReportsSlidesService: Service.Flow.Tour.ReportsSlides;
	@service('model-manager/boards') declare mmBoardsService: Service.ModelManager.Boards;
	@service('model-manager/reports') declare mmReportsService: Service.ModelManager.Reports;
	@service('model-manager/report-slides')
	declare mmReportSlidesService: Service.ModelManager.ReportSlides;
	@service('model-manager/report-slide-fragments')
	declare mmReportSlideFragmentsService: Service.ModelManager.ReportSlideFragments;

	@tracked sortedSlides: ReportSlide[] = [];
	_nextSortedSlides: ReportSlide[] | null = null; // only non-null inbetween when we are resetting slide list rendering (forceSortedSlideUpdate)

	@tracked isAddButtonShownOnList = true;

	availableTourIds = [TourId.REPORTS__CREATE_NEW_SLIDE, TourId.REPORTS__REORDER_SLIDES];

	slidesTourSettings: SlidesTourSettings[] = [
		{
			id: TourId.REPORTS__CREATE_NEW_SLIDE,
			checkStartConditions: this.checkCreateNewSlideTourStartConditions.bind(this),
		},
		{
			id: TourId.REPORTS__REORDER_SLIDES,
			checkStartConditions: this.checkReorderSlidesTourStartConditions.bind(this),
		},
	];

	constructor(owner: Owner, args: Args) {
		super(owner, args);

		this.sortedSlides = Array.from(this.args.model.sortedSlides);

		schedule('afterRender', () => {
			this.setupTourBindings();
		});
	}

	setupTourBindings() {
		this.slidesTourSettings.forEach((tourSetting) => {
			this.flowTourReportsSlidesService.bindTourCheck(tourSetting.id, this, () => {
				if (tourSetting.checkStartConditions()) {
					this.flowTourReportsSlidesService.startTour(tourSetting.id);
				}
			});
		});
	}

	moveItem(prevPosition: number, position: number): void {
		const allSlides = Array.from(this.sortedSlides),
			movedSlide = this.sortedSlides[prevPosition];

		if (movedSlide) {
			const movedIndex = allSlides.indexOf(movedSlide);
			allSlides.splice(movedIndex, 1);
			allSlides.splice(position, 0, movedSlide);
		}

		const reorderedSlides = this.mmReportsService.reorderSlides(this.args.model, allSlides);
		this.forceSortedSlideUpdate(reorderedSlides);

		this.flowTourReportsSlidesService.endTour(TourId.REPORTS__REORDER_SLIDES);
		this.flowTourReportsSlidesService.checkTour();
	}

	forceSortedSlideUpdate(allSlides: ReportSlide[]): void {
		// ugly way to force the changed array to correctly update components
		this._nextSortedSlides = allSlides;
		this.sortedSlides = [];
		schedule('afterRender', () => {
			this.sortedSlides = allSlides;
			this._nextSortedSlides = null;
		});
	}

	private setupSortable(): void {
		this.listEl.sortable({
			items: 'li:not(.add)',
			axis: 'y',
			containment: 'parent',
			tolerance: 'pointer', // otherwise containment parent cannot handle 1st or last sorts
			placeholder: {
				element: () => {
					return $(
						'<li class="sort-placeholder"><a><div class="board-image-preview"></div></a></li>',
					);
				},
				update: function () {
					return;
				},
			},
			start: function (_event: JQuery.Event, ui: jQueryUIInstance) {
				ui.helper.addClass('sort-helper active-helper'); //for animation
				ui.helper.data('current-position', ui.helper.index());
			},
			beforeStop: function (_event: JQuery.Event, ui: jQueryUIInstance) {
				ui.helper.removeClass('sort-helper active-helper');
			},
			update: (_event: JQuery.Event, ui: jQueryUIInstance) => {
				const prevIndex = ui.item.data('current-position') as number,
					index = ui.item.index();
				this.moveItem(prevIndex, index);
			},
		});
	}

	openNewSlideOptions(attachTo: HTMLElement): void {
		this.popupService
			.createContext(ReportsPopupSlideTypeComponent, attachTo, {
				popupClass: 'popup-type-report-slide-type',
				offset: 15,
			})
			.open({
				addNewSlideLayoutsDisabled: this.args.model.addNewSlideLayoutsDisabled,
				onSelect: (type: NewReportSlideLayout) => {
					return this.addNewSlide(type);
				},
			});
	}

	private destroySortable(): void {
		try {
			this.listEl.sortable('destroy');
		} catch (_e) {
			// skip
		}
	}

	@action
	updateAddBtnPosition(): void {
		const scrollContentNode: HTMLElement | null =
			this.node.querySelector('.scrollable-content');

		if (scrollContentNode) {
			scrollContentNode.style.height = '100%';
			const addBtnNode = this.node.querySelector<HTMLElement>('.add'),
				addBtnNodeHeight = addBtnNode?.offsetHeight || 0,
				scrollContentElHeight = scrollContentNode.offsetHeight,
				listNodeHeight = this.listEl.get(0)!.offsetHeight,
				isShownOnList = listNodeHeight + addBtnNodeHeight <= scrollContentElHeight;

			// prevent updating while moving elements
			if (listNodeHeight <= addBtnNodeHeight) {
				return;
			}

			if (!isShownOnList) {
				scrollContentNode.style.height = `calc(100% - ${addBtnNodeHeight}px)`;
			}

			this.isAddButtonShownOnList = isShownOnList;
		}
	}

	@action
	updateSlides(): void {
		// only need to handle slide deletion. adding only happens in this component itself
		const modelSlides = this.args.model.slides,
			componentSlides = this.sortedSlides;

		const added: ReportSlide[] = [];
		modelSlides.forEach((slide) => {
			// search by model bcos it might not be saved yet
			if (!componentSlides.find((item) => item.model === slide.model)) {
				added.push(slide);
			}
		});

		const deleted: ReportSlide[] = [];
		componentSlides.forEach((slide) => {
			// search by model bcos it might not be saved yet
			if (!modelSlides.find((item) => item.model === slide.model)) {
				deleted.push(slide);
			}
		});

		if (added.length || deleted.length) {
			let newSlides = Array.from(componentSlides).filter((slide) => !deleted.includes(slide));
			added.forEach((slide) => {
				if (!newSlides.includes(slide)) {
					newSlides.push(slide);
				}
			});
			newSlides = sortBy(newSlides, 'order');
			this.forceSortedSlideUpdate(newSlides);
			next(() => {
				this.updateAddBtnPosition();
			});
		}
	}

	@action
	openSlide(slide: ReportSlide): void {
		this.args.onOpenSlide(slide);
	}

	@action
	addNewSlide(type: number) {
		return this.args.onAddNewSlide(type);
	}

	@action
	addSlide(e: PointerEvent): void {
		const target = e.target as HTMLElement;
		const typeSelector = target?.closest<HTMLElement>('.type--selector');

		if (typeSelector) {
			this.openNewSlideOptions(typeSelector);
		} else {
			void this.addNewSlide(NewReportSlideLayout.COLUMNS_1);
		}
	}

	@action
	setupView(node: HTMLElement): void {
		this.node = node;
		this.listEl = $(node).find('ol');
		this.setupSortable();
		this.updateAddBtnPosition();
	}

	@action
	teardownView(): void {
		this.destroySortable();

		this.slidesTourSettings.forEach((tourSetting) => {
			this.flowTourReportsSlidesService.unbindTourCheck(tourSetting.id, this);
		});
	}

	private checkCreateNewSlideTourStartConditions(): boolean {
		return this.args.model.slides.length <= 1;
	}

	private checkReorderSlidesTourStartConditions(): boolean {
		return this.args.model.slides.length >= 2;
	}
	<template>
		<div
			class="report_nav"
			{{didInsert this.setupView}}
			{{didUpdate this.updateSlides @model.slides}}
			{{willDestroy this.teardownView}}
			{{onWindowEvents this.updateAddBtnPosition "scroll resize orientationchange" 200}}
		>

			<div class="scrollable-content">
				<ol>
					{{#each this.sortedSlides as |slide|}}
						<li
							data-id={{slide.id}}
							class={{if (eq slide.id @selectedSlide.id) "selected"}}
						>
							<a class="open" {{on "click" (fn this.openSlide slide)}}>
								<BoardImagePreviewComponent
									@image={{slide.shownImage}}
									@fallbackImage={{slide.fallbackImage}}
									@color={{slide.shownColor}}
								/>
								<CommonUiIconComponent
									@name="List"
									@size="xs"
									@class="drag-handle"
								/>
							</a>
							<a class="remove" {{on "click" (fn @onRemoveSlide slide)}}>
								<CommonUiIconComponent @name="Close" @size="xs" />
							</a>
						</li>
					{{/each}}

					{{#if this.isAddButtonShownOnList}}
						<li class="add">
							<ReportsEditorNavigationAddComponent @onAddSlide={{this.addSlide}} />
						</li>
					{{/if}}
				</ol>
			</div>

			{{#unless this.isAddButtonShownOnList}}
				<div class="add add--with-shadow">
					<ReportsEditorNavigationAddComponent @onAddSlide={{this.addSlide}} />
				</div>
			{{/unless}}

		</div>
	</template>
}
